/**
 * DispatchService — BQC3 slips + reassignments + availability writes.
 * Per docs/bqc-migration/SCHEMA.md. DS numbers come ONLY from
 * MfgSettingsService.allocateDsNumber (gaps allowed, duplicates never);
 * printing reads persisted rows only.
 */
import { createSharePointClient } from "./spClient";
import { MFG_SITE_URL } from "./mfgSite";
import { mfgSettingsService } from "./MfgSettingsService";
import { notifyPortalStale } from "../../modules/sewer-portal/portalStale";

// The printable form lives in slipTemplate; re-exported so existing callers
// (and the screens) keep one import site for everything slip-shaped.
export { buildSlipHtml, slipDocTitle, slipLiveSets, SLIP_MAX_ROWS } from "./slipTemplate";

const SLIPS = "iSyn2_DispatchSlips";
const REASSIGN = "iSyn2_Reassignments";
const UNAVAIL = "iSyn2_SewerUnavailability";

/**
 * The amend columns are newer than the list, so a tenant provisioned before
 * they existed would 400 the whole read if they were selected unconditionally.
 * They are requested here and the read falls back to the original set on
 * failure — see listSlips.
 */
const SLIP_CORE =
  "Id,dsNumber,sewer,sewerCode,wipIds,totalSets,totalPieces,slipStatus,generatedBy,generatedAt," +
  "signatoryDispatchedBy,signatoryPreparedBy,printCount,lastPrintedAt";
const SLIP_SELECT_V2 = `${SLIP_CORE},amendedBy,amendedAt,amendNote,revision,syncedToWorkbookAt,historical`;
// grn is newer still than the amend columns — its own fallback tier, same
// reason SLIP_SELECT_V2 has one over SLIP_CORE (see listSlips).
const SLIP_SELECT_V3 = `${SLIP_SELECT_V2},grn`;

export interface ISlip {
  spId: number;
  dsNumber: string;
  sewer: string;
  sewerCode: string;
  wipIds: string[];
  totalSets: number;
  totalPieces: number;
  slipStatus: "Issued" | "Cancelled";
  generatedBy: string;
  generatedAt: string;
  signatoryDispatchedBy: string;
  signatoryPreparedBy: string;
  printCount: number;
  lastPrintedAt?: string;
  /** Set once the slip's contents have been changed after issue. */
  amendedBy?: string;
  amendedAt?: string;
  amendNote?: string;
  /** 0 or absent for a slip still exactly as issued. */
  revision?: number;
  /** Last time this slip was written into the ASSIGNING outbox CSV — see
   * assigningOutbox.ts. Absent means never exported. */
  syncedToWorkbookAt?: string;
  /** True for a slip imported from the legacy workbook's own BQC3 sheet
   * (historicalImport.ts) rather than issued by this app — real history
   * from before i-Synergy existed, not a new dispatch. Receiving and QC
   * exclude these from their pending queues (there is no scan history for
   * any of it here); Dispatch's Cut · Ready WIPs table still shows them,
   * labelled as imported. */
  historical?: boolean;
  /** Goods Receipt Number — one per slip, allocated by Receiving.tsx's
   * record() the first time ANY of this slip's WIPs is received (see
   * MfgSettingsService.allocateGrnNumber). Absent until then; never
   * reassigned once set. Reaches the legacy RECEIVING sheet's own column J
   * through receivingOutbox.ts. */
  grn?: string;
}

export class DispatchService {
  private readonly sp = createSharePointClient(MFG_SITE_URL);
  private types = new Map<string, string>();

  private async type(list: string): Promise<string> {
    const hit = this.types.get(list);
    if (hit) return hit;
    const r = await this.sp.raw<{ d: { ListItemEntityTypeFullName: string } }>(
      `/_api/web/lists/getbytitle('${list}')?$select=ListItemEntityTypeFullName`);
    this.types.set(list, r.d.ListItemEntityTypeFullName);
    return r.d.ListItemEntityTypeFullName;
  }

  private mapSlip(r: Record<string, unknown>): ISlip {
    return {
      spId: r.Id as number,
      dsNumber: String(r.dsNumber ?? ""),
      sewer: String(r.sewer ?? ""),
      sewerCode: String(r.sewerCode ?? ""),
      wipIds: String(r.wipIds ?? "").split(";").map(s => s.trim()).filter(Boolean),
      totalSets: Number(r.totalSets ?? 0),
      totalPieces: Number(r.totalPieces ?? 0),
      slipStatus: (r.slipStatus as ISlip["slipStatus"]) ?? "Issued",
      generatedBy: String(r.generatedBy ?? ""),
      generatedAt: String(r.generatedAt ?? ""),
      signatoryDispatchedBy: String(r.signatoryDispatchedBy ?? ""),
      signatoryPreparedBy: String(r.signatoryPreparedBy ?? ""),
      printCount: Number(r.printCount ?? 0),
      lastPrintedAt: r.lastPrintedAt ? String(r.lastPrintedAt) : undefined,
      amendedBy: r.amendedBy ? String(r.amendedBy) : undefined,
      amendedAt: r.amendedAt ? String(r.amendedAt) : undefined,
      amendNote: r.amendNote ? String(r.amendNote) : undefined,
      revision: r.revision === undefined || r.revision === null ? undefined : Number(r.revision),
      syncedToWorkbookAt: r.syncedToWorkbookAt ? String(r.syncedToWorkbookAt) : undefined,
      ...(r.historical === true ? { historical: true } : {}),
      grn: r.grn ? String(r.grn) : undefined,
    };
  }

  private async readSlips(filter?: string): Promise<ISlip[]> {
    const read = async (select: string): Promise<ISlip[]> => {
      const rows = await this.sp.getListItems<Record<string, unknown>>(SLIPS, { select, filter, orderby: "Id desc" });
      return rows.map(r => this.mapSlip(r));
    };
    try {
      return await read(SLIP_SELECT_V3);
    } catch (e) {
      console.warn("[DispatchService] slip read without grn column — use \"Add missing columns\" on the Migration Valve to add it.", e);
      try {
        return await read(SLIP_SELECT_V2);
      } catch (e2) {
        // A site provisioned before the amend columns existed rejects the
        // whole $select. Losing the amendment history is a great deal
        // better than losing the slip archive, so it degrades and says so
        // once.
        console.warn("[DispatchService] slip read without amendment columns — use \"Add missing columns\" on the Migration Valve to add them (\"Set up lists\" only creates whole lists, it never adds a column to one that already exists).", e2);
        return read(SLIP_CORE);
      }
    }
  }

  public async listSlips(): Promise<ISlip[]> {
    return this.readSlips();
  }

  /**
   * Same shape and the same $select fallback tiers as `listSlips()`,
   * filtered server-side to slips generated on or after `sinceIso` —
   * FOR THE SUBCONTRACTOR PAYROLL MODULE ONLY. Every live station (BQC3's
   * own screen, Receiving, QC) needs the WHOLE archive — a slip issued two
   * years ago can still be the one a WIP's sewer is attributed from — so
   * `listSlips()` itself is untouched and this is a second, narrower method
   * rather than a parameter that could be passed the wrong thing by
   * accident. See `sharedWipCache.ts`'s `listSlipsSinceCached` for why
   * payroll gets to narrow this at all.
   */
  public async listSlipsSince(sinceIso: string): Promise<ISlip[]> {
    return this.readSlips(`generatedAt ge datetime'${sinceIso}T00:00:00Z'`);
  }

  /**
   * Hard-deletes one slip.
   *
   * Only ever used by the test-data purge — a real slip is CANCELLED, never
   * deleted, because a gap in the DS sequence with nothing to explain it is
   * worse than a cancelled slip somebody can read. Kept private to that use by
   * convention rather than by the type system, so if a second caller ever
   * appears this comment is the argument it has to answer.
   */
  public async deleteSlip(spId: number): Promise<void> {
    await this.sp.deleteItem(SLIPS, spId);
  }

  /** WIPs already on an Issued slip (validation ladder rung 2). */
  public static dispatchedWips(slips: ISlip[]): Map<string, string> {
    const m = new Map<string, string>();
    for (const s of slips) if (s.slipStatus === "Issued") for (const w of s.wipIds) m.set(w, s.dsNumber);
    return m;
  }

  /**
   * Creates one Issued slip per group, whatever the group's size — a group
   * is one sewer's whole batch, not a printed page. SLIP_MAX_ROWS bounds the
   * PRINTED FORM (slipTemplate.ts's buildSlipHtml continues onto additional
   * pages past it, same DS number), not how many WIPs a slip may hold.
   * Fails closed on empty signatories (SCHEMA §5). Sequential; a burned DS
   * (allocated but slip create failed) is never reused.
   *
   * THE POOL IS RE-CHECKED HERE, not trusted from the caller. BQC3 builds the
   * dispatch-ready pool by excluding WIPs already on an Issued slip, using the
   * copy of the slip list its screen is holding — which is as old as the last
   * refresh. Two dispatchers pressing Generate DS in the same window therefore
   * both see the same bundle as undispatched, and both put it on a slip: one
   * physical bundle, two DS numbers, two sewers expecting it, and the mistake
   * discovered on paper days later. That is precisely the duplicate this
   * module exists to prevent, and it is the one class of error that cannot be
   * fixed from the app afterwards.
   *
   * So the slip list is read once, fresh, immediately before anything is
   * allocated, and any WIP already carried by an Issued slip is dropped from
   * its group and reported back rather than dispatched again. A group left
   * empty by that is skipped without burning a number.
   *
   * This narrows the window from minutes to milliseconds; it does not close
   * it. Two calls can still interleave between this read and their writes.
   * Closing it completely needs a per-WIP claim that can be taken atomically —
   * SharePoint offers nothing to lock a list row with — so what is left is a
   * far smaller window, and a check that catches every duplicate originating
   * from a stale SCREEN, which is where they actually come from.
   */
  public async createSlips(
    groups: { sewer: string; sewerCode?: string; wipIds: string[]; totalSets: number;
      /** WIPs in `wipIds` that are QC's returned repairs. They are already on an
       *  earlier slip BY DESIGN, so the already-dispatched guard skips them. */
      repairWipIds?: string[] }[],
    generatedBy: string,
    onProgress?: (msg: string) => void
  ): Promise<{
    created: ISlip[];
    failed: { sewer: string; error: string }[];
    /** WIPs dropped because another dispatcher had already put them on a slip
     *  between this screen's last refresh and this call. Not an error — the
     *  bundle IS dispatched, just not by this run — but the dispatcher has to
     *  be told, or they will look for it in the printed slips and not find it. */
    heldBack: { wipId: string; dsNumber: string }[];
  }> {
    const sigD = (await mfgSettingsService.get("signatoryDispatchedBy")) ?? "";
    const sigP = (await mfgSettingsService.get("signatoryPreparedBy")) ?? "";
    if (!sigD.trim() || !sigP.trim()) {
      throw new Error("Slip signatories are not set — fill them in the Migration Valve settings first (fail closed).");
    }

    // Fresh, and deliberately NOT best-effort: dispatching without knowing what
    // is already out is the exact failure this guard exists for, so a read
    // failure stops the run rather than falling through to the old behaviour.
    const liveSlips = await this.listSlips();
    const alreadyOut = DispatchService.dispatchedWips(liveSlips);
    const heldBack: { wipId: string; dsNumber: string }[] = [];
    const vetted = groups.map(g => {
      const keep: string[] = [];
      const repairs = new Set(g.repairWipIds ?? []);
      for (const w of g.wipIds) {
        const ds = repairs.has(w) ? undefined : alreadyOut.get(w);
        if (ds) heldBack.push({ wipId: w, dsNumber: ds });
        else keep.push(w);
      }
      // Sets are re-derived from what survived, so a slip's own total can
      // never describe more bundles than it carries.
      const share = g.wipIds.length > 0 ? g.totalSets / g.wipIds.length : 0;
      return { ...g, wipIds: keep, totalSets: Math.round(share * keep.length) };
    }).filter(g => g.wipIds.length > 0);

    const type = await this.type(SLIPS);
    const created: ISlip[] = [];
    const failed: { sewer: string; error: string }[] = [];
    for (const g of vetted) {
      try {
        const dsNumber = await mfgSettingsService.allocateDsNumber(generatedBy);
        onProgress?.(`Allocated ${dsNumber} for ${g.sewer}`);
        const body = {
          __metadata: { type },
          Title: dsNumber,
          dsNumber,
          sewer: g.sewer,
          sewerCode: g.sewerCode ?? "",
          wipIds: g.wipIds.join(";"),
          totalSets: g.totalSets,
          totalPieces: g.totalSets * 3,
          slipStatus: "Issued",
          generatedBy,
          generatedAt: new Date().toISOString(),
          signatoryDispatchedBy: sigD,
          signatoryPreparedBy: sigP,
          printCount: 0,
        };
        const res = await this.sp.createItem<{ Id: number }>(SLIPS, body);
        created.push(this.mapSlip({ ...body, Id: res.Id }));
        notifyPortalStale();
      } catch (e) {
        failed.push({ sewer: g.sewer, error: e instanceof Error ? e.message : String(e) });
      }
    }
    return { created, failed, heldBack };
  }

  /**
   * Creates one historical Issued slip per group — see historicalImport.ts
   * for how a group is built from the DS# tally's workbook-only rows.
   *
   * Deliberately NOT createSlips(): these numbers already exist in the
   * paper book, so nothing is allocated from the live DS counter (that
   * would burn a real number on a slip that isn't a new dispatch), and
   * empty signatories do not fail closed (that rule exists to gate
   * PRINTING a real slip; a historical row is never printed). The caller
   * is expected to have already confirmed the `historical` column is
   * provisioned — see Migration Valve's "Add missing columns" — since a
   * slip written without it would be indistinguishable from an app-issued
   * one and wrongly show up as pending on Receiving and QC.
   */
  public async importHistoricalSlips(
    specs: { dsNumber: string; sewer: string; wipIds: string[]; totalSets: number; generatedAt: string }[],
    importedBy: string,
    onProgress?: (msg: string) => void
  ): Promise<{ created: ISlip[]; failed: { dsNumber: string; error: string }[] }> {
    const type = await this.type(SLIPS);
    const created: ISlip[] = [];
    const failed: { dsNumber: string; error: string }[] = [];
    // Bounded-concurrency creates — a batch's specs go into independent
    // rows (no read-modify-write between them), so there is nothing to
    // serialize for; six workers pull from a shared cursor instead of one
    // spec waiting on the last, the same pattern ColorReferenceService's
    // seeding uses. A 45-slip batch that took the better part of a minute
    // one at a time comes down to a few seconds.
    let next = 0;
    const worker = async (): Promise<void> => {
      while (next < specs.length) {
        const spec = specs[next++];
        try {
          const body = {
            __metadata: { type },
            Title: spec.dsNumber,
            dsNumber: spec.dsNumber,
            sewer: spec.sewer,
            sewerCode: "",
            wipIds: spec.wipIds.join(";"),
            totalSets: spec.totalSets,
            totalPieces: spec.totalSets * 3,
            slipStatus: "Issued",
            generatedBy: importedBy,
            generatedAt: spec.generatedAt,
            signatoryDispatchedBy: "",
            signatoryPreparedBy: "",
            printCount: 0,
            historical: true,
          };
          const res = await this.sp.createItem<{ Id: number }>(SLIPS, body);
          created.push(this.mapSlip({ ...body, Id: res.Id }));
          onProgress?.(`Imported ${spec.dsNumber} — ${spec.wipIds.length} WIP(s)`);
        } catch (e) {
          failed.push({ dsNumber: spec.dsNumber, error: e instanceof Error ? e.message : String(e) });
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(6, specs.length) }, worker));
    return { created, failed };
  }

  /**
   * Record a print, and bring the stored totals back in line with the paper.
   *
   * `totalSets` is a snapshot taken when the slip was issued, while every
   * figure on the printed form is read live — so editing a bundle's quantity
   * in iSyn_WIPs leaves the column behind (see slipLiveSets). The document is
   * already fixed by deriving its total from its rows; this stops the LIST
   * from going on saying something the paper contradicts, for anyone reading
   * it in SharePoint rather than in the app.
   *
   * Healed HERE and nowhere else, deliberately. Printing is a moment somebody
   * chose, once, and there is already a write going out — so this costs
   * nothing and cannot turn into a background writer racing itself across two
   * open tabs. `sets` omitted (a caller that cannot price the rows) leaves
   * the stored figures exactly as they were: a total nobody can recompute is
   * better left alone than overwritten with a guess.
   */
  public async bumpPrinted(slip: ISlip, sets?: number): Promise<void> {
    const heal = typeof sets === "number" && Number.isFinite(sets)
      && sets > 0 && sets !== slip.totalSets;
    await this.sp.updateItem(SLIPS, slip.spId, {
      __metadata: { type: await this.type(SLIPS) },
      printCount: slip.printCount + 1,
      lastPrintedAt: new Date().toISOString(),
      ...(heal ? { totalSets: sets, totalPieces: sets * 3 } : {}),
    });
  }

  /**
   * Change an issued slip's contents — its WIPs, a corrected DS number, or
   * (the emergency handover, Mikko 2026-09-24) the sewer it belongs to.
   *
   * The rules about WHAT may be added/removed/renumbered/handed over live in
   * slipEdit.ts and slipReassign.ts and are settled (including the
   * duplicate-DS check and the handover offer) before this is called; this
   * writes the result and the audit trail. `next.dsNumber`, when given,
   * updates BOTH Title and dsNumber — the two columns createSlips() sets
   * together at allocation time, so they never drift apart on an edit either.
   * `next.sewer` renames the slip itself; the caller is the one who reassigns
   * every WIP on it, because that write goes through iSyn2_Assignments, a
   * different list this method knows nothing about.
   *
   * Falls back to writing the contents alone if the amendment columns are not
   * provisioned — the operator's edit lands either way, and the caller is told
   * the trail is missing rather than the whole save failing on a column that
   * only exists to record it. `auditWritten: false` is the signal to say so.
   */
  public async amendSlip(
    slip: ISlip,
    next: {
      wipIds: string[]; totalSets: number; totalPieces: number; dsNumber?: string;
      sewer?: string; sewerCode?: string;
    },
    by: string,
    note: string
  ): Promise<{ auditWritten: boolean }> {
    const type = await this.type(SLIPS);
    const dsNumber = next.dsNumber?.trim();
    const core: Record<string, unknown> = {
      __metadata: { type },
      wipIds: next.wipIds.join(";"),
      totalSets: next.totalSets,
      totalPieces: next.totalPieces,
    };
    if (dsNumber && dsNumber !== slip.dsNumber.trim()) {
      core.Title = dsNumber;
      core.dsNumber = dsNumber;
    }
    if (next.sewer && next.sewer.trim() !== slip.sewer.trim()) {
      core.sewer = next.sewer.trim();
      core.sewerCode = next.sewerCode ?? "";
    }
    try {
      await this.sp.updateItem(SLIPS, slip.spId, {
        ...core,
        amendedBy: by,
        amendedAt: new Date().toISOString(),
        amendNote: note,
        revision: (slip.revision ?? 0) + 1,
      });
      return { auditWritten: true };
    } catch {
      await this.sp.updateItem(SLIPS, slip.spId, core);
      return { auditWritten: false };
    }
  }

  public async cancelSlip(slip: ISlip, by: string, note: string): Promise<void> {
    await this.sp.updateItem(SLIPS, slip.spId, {
      __metadata: { type: await this.type(SLIPS) },
      slipStatus: "Cancelled",
      cancelledBy: by,
      cancelledAt: new Date().toISOString(),
      cancelNote: note,
    });
  }

  /** Override event. Caller ALSO writes the iSyn2_Assignments REASSIGN row. */
  public async logReassignment(args: {
    wipId: string; fromSewer: string; toSewer: string; reasonCode: string;
    reasonNote?: string; by: string; dsNumber?: string; unavailabilityLogged: boolean;
  }): Promise<void> {
    await this.sp.createItem(REASSIGN, {
      __metadata: { type: await this.type(REASSIGN) },
      Title: args.wipId,
      wipId: args.wipId,
      fromSewer: args.fromSewer,
      toSewer: args.toSewer,
      reasonCode: args.reasonCode,
      reasonNote: args.reasonNote ?? "",
      reassignedBy: args.by,
      reassignedAt: new Date().toISOString(),
      dsNumber: args.dsNumber ?? "",
      unavailabilityLogged: args.unavailabilityLogged,
    });
  }

  public async markUnavailable(args: {
    sewer: string; reason: "HEALTH" | "ABSENT" | "OTHER"; untilDate: string | null; note?: string; by: string;
  }): Promise<void> {
    await this.sp.createItem(UNAVAIL, {
      __metadata: { type: await this.type(UNAVAIL) },
      Title: args.sewer,
      sewer: args.sewer,
      fromDate: new Date().toISOString(),
      untilDate: args.untilDate,
      reason: args.reason,
      note: args.note ?? "",
      loggedBy: args.by,
      loggedAt: new Date().toISOString(),
    });
  }

  public async clearUnavailability(spId: number, by: string): Promise<void> {
    await this.sp.updateItem(UNAVAIL, spId, {
      __metadata: { type: await this.type(UNAVAIL) },
      clearedBy: by,
      clearedAt: new Date().toISOString(),
    });
  }

  /** Marks slips as exported to the ASSIGNING outbox — called once the CSV
   * upload has actually succeeded, never before, so a failed upload leaves
   * the slip eligible for the next attempt rather than silently skipped. */
  public async markSyncedToWorkbook(spIds: readonly number[], at: string): Promise<void> {
    const type = await this.type(SLIPS);
    for (const spId of spIds) {
      await this.sp.updateItem(SLIPS, spId, { __metadata: { type }, syncedToWorkbookAt: at });
    }
  }

  /** Persists a freshly-allocated GRN onto a slip — called once, the first
   * time any of its WIPs is received (Receiving.tsx's record()). See
   * ISlip.grn and MfgSettingsService.allocateGrnNumber for the allocation
   * itself; this is only the write. */
  public async assignGrn(spId: number, grn: string): Promise<void> {
    await this.sp.updateItem(SLIPS, spId, { __metadata: { type: await this.type(SLIPS) }, grn });
  }

  public async reassignedTodayCount(): Promise<number> {
    const midnight = new Date(); midnight.setHours(0, 0, 0, 0);
    const rows = await this.sp.getListItems<{ Id: number }>(REASSIGN, {
      select: "Id", filter: `reassignedAt ge datetime'${midnight.toISOString()}'`,
    });
    return rows.length;
  }
}

export const dispatchService = new DispatchService();
